import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Paper } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { PackEntry } from 'types/shared';
import { useTestCreatives } from '../../api';
import { useFilterOptions } from '../../api/getFilterOptions';
import {
  useCreativesByPack,
  useCreativeColumnVisibility,
  useFiltersActive,
  useHypothesisGrouping,
  useOptionalFormContext,
  useOrderedHypothesisGroups,
  useSavedCreativeFilters,
  useTestCreativesFormState,
  useTestCreativesViewState,
  NO_HYPOTHESIS_KEY,
  MIXED_PACK_KEY,
  type HypothesisPack,
} from '../../hooks';
import {
  DEFAULT_SAVED_CREATIVE_FILTERS,
  type CreativeDnDMovePayload,
  type HypothesisGroup,
  type SavedCreativeFilters,
} from '../../types/hypothesis';
import {
  areSavedCreativeFiltersEqual,
  getSavedCreativeFiltersEntry,
  normalizeSavedCreativeFilters,
  pickSavedCreativeFilters,
} from '../../utils/savedCreativeFilters';
import CreativesFilterBar from './CreativesFilterBar';
import TestCreativesContent from './TestCreativesContent';
import TestCreativesHeader from './TestCreativesHeader';
import LoadingOverlay from './LoadingOverlay';
import { ALL_COLUMN_DEFS } from './creativeColumns';
import {
  getMixedPackCreativeIds,
  getTestCreativesIdToHypothesis,
  NO_HYPOTHESIS_TAG,
} from '../../utils/hypothesisPackUtils';
import {
  adjustActivePackIndexAfterRemoval,
  applyPackCreativesMove,
  pruneEmptyPacks,
} from './applyPackCreativesMove';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';

const EMPTY_PACKS: PackEntry[] = [];
const EMPTY_NO_HYPOTHESIS_PACKS: HypothesisPack[] = [];

interface TestCreativesListProps {
  defaultApp?: string;
}

const TestCreativesList: FC<TestCreativesListProps> = ({ defaultApp }) => {
  const { app, network, selectedCreatives, updateSelection } =
    useTestCreativesViewState({ defaultApp });

  const formContext = useOptionalFormContext();
  const packsWithCreatives =
    (formContext?.watch('packsWithCreatives') as PackEntry[] | undefined) ??
    EMPTY_PACKS;

  const {
    filters,
    uploadedFilter,
    groupByHypothesis,
    unpackedOrderByHypothesis,
    setHypothesis,
    setSearch,
    setFlow,
    setCustomer,
    setTechnology,
    setTpcFinishDate,
    setStatus,
    setUploadedFilter,
    setGroupByHypothesis,
    setUnpackedOrderByHypothesis,
    applyCreativeFilters,
    resetFilters,
  } = useTestCreativesFormState();

  const currentSavedFilters = useMemo<SavedCreativeFilters>(
    () =>
      pickSavedCreativeFilters({
        search: filters.search,
        uploaded: uploadedFilter,
        hypothesis: filters.hypothesis,
        flow: filters.flow,
        customer: filters.customer,
        technology: filters.technology,
        tpcFinishDate: filters.tpcFinishDate,
        status: filters.status,
      }),
    [filters, uploadedFilter],
  );

  const {
    hasSaved: saveFiltersHasSaved,
    enabled: saveFiltersEnabled,
    isDirty: saveFiltersDirty,
    savedAtLabel,
    savedSummaryLines,
    enableSaved,
    disableSaved,
    updateSaved,
    clearSaved,
  } = useSavedCreativeFilters({
    app,
    currentFilters: currentSavedFilters,
  });

  const handleEnableSaveFilters = useCallback(() => {
    const restored = enableSaved();
    if (restored) {
      applyCreativeFilters(restored);
    }
  }, [applyCreativeFilters, enableSaved]);

  const handleDisableSaveFilters = useCallback(() => {
    const remaining = disableSaved();
    if (remaining) {
      applyCreativeFilters(remaining);
    }
  }, [applyCreativeFilters, disableSaved]);

  const handleClearSaveFilters = useCallback(() => {
    clearSaved();
  }, [clearSaved]);

  // Hydrate once per app on enter when the form still has defaults.
  // Process/app resets also call resolveTestCreativesViewState. Skip when the
  // session already has non-default filters (e.g. remount after tab switch).
  const hydratedAppRef = useRef<string | null>(null);
  useEffect(() => {
    if (!app) return;
    if (hydratedAppRef.current === app) return;
    hydratedAppRef.current = app;

    const entry = getSavedCreativeFiltersEntry(app);
    if (!entry?.enabled) return;
    if (
      !areSavedCreativeFiltersEqual(
        currentSavedFilters,
        DEFAULT_SAVED_CREATIVE_FILTERS,
      )
    ) {
      return;
    }
    applyCreativeFilters(normalizeSavedCreativeFilters(entry.filters));
  }, [app, applyCreativeFilters, currentSavedFilters]);

  const { data: displayCreatives = [], isLoading, isFetching } = useTestCreatives({
    params: {
      app,
      hypothesis: filters.hypothesis,
      customer: filters.customer ?? undefined,
      flow: filters.flow,
      technology: filters.technology,
      status: filters.status ?? undefined,
      query: filters.search || undefined,
      tpc_finish_date: filters.tpcFinishDate ?? undefined,
      uploaded: uploadedFilter,
    },
    config: { enabled: !!app },
  });

  const hasPacks = packsWithCreatives.length > 0;
  const { data: unfilteredCreatives = [] } = useTestCreatives({
    params: { app },
    config: { enabled: !!app && hasPacks },
  });

  const { data: filterOptionsData } = useFilterOptions({
    params: { app, network },
    config: { enabled: !!app },
  });

  const filterOptions = useMemo(
    () => ({
      hypotheses: filterOptionsData?.hypothesis ?? [],
      customers: filterOptionsData?.customer ?? [],
      flows: filterOptionsData?.flow ?? [],
      technologies: filterOptionsData?.technology ?? [],
    }),
    [filterOptionsData],
  );

  const {
    visibilityModel,
    columnOrder,
    toggleColumn,
    moveColumn,
    resetDefaults,
  } = useCreativeColumnVisibility();

  const packFormActionsEnabled = hasPacks && !!formContext;
  const dragEnabled = packFormActionsEnabled;
  const canEditPackTag = packFormActionsEnabled;

  const handleActivatePack = useCallback(
    (packIndex: number) => {
      if (!formContext) return;

      const targetPack = packsWithCreatives[packIndex];
      formContext.setValue('filterCreativeByName', '');
      formContext.setValue('activePacksWithCreatives', packIndex);
      formContext.setValue('adType', targetPack?.adType ?? '');
      formContext.setValue('testCreativesTab', 'pack');
    },
    [formContext, packsWithCreatives],
  );

  const packTagsByIndex = useMemo(
    () => packsWithCreatives.map((pack) => pack.tag),
    [packsWithCreatives],
  );

  const creativeNameTagsById = useMemo(() => {
    const mixedIds = getMixedPackCreativeIds(packsWithCreatives);
    if (mixedIds.size === 0) return undefined;

    const idToHypothesis = getTestCreativesIdToHypothesis();
    const map = new Map<string, string[]>();
    for (const id of mixedIds) {
      const hypothesisLabel = idToHypothesis.get(id) ?? NO_HYPOTHESIS_TAG;
      map.set(id, [hypothesisLabel]);
    }
    return map;
  }, [packsWithCreatives]);

  const visibleColumns = useMemo<GridColDef[]>(() => {
    const dataColumns = columnOrder
      .filter((key) => visibilityModel[key])
      .filter((key) => key !== 'dragHandle')
      .map((key) => ALL_COLUMN_DEFS[key])
      .filter(Boolean);

    if (!packFormActionsEnabled) return dataColumns;
    return [ALL_COLUMN_DEFS.dragHandle, ...dataColumns];
  }, [
    columnOrder,
    visibilityModel,
    packFormActionsEnabled,
  ]);

  const { packsByHypothesis, unpackedCreatives } = useCreativesByPack(
    packsWithCreatives,
    unfilteredCreatives,
    displayCreatives,
  );

  const noHypothesisPacks = useMemo(
    () => packsByHypothesis.get(NO_HYPOTHESIS_KEY) ?? EMPTY_NO_HYPOTHESIS_PACKS,
    [packsByHypothesis],
  );

  const {
    groups: unpackedGroups,
    noHypothesis,
    hypothesisCount: unpackedHypothesisCount,
  } = useHypothesisGrouping(unpackedCreatives);

  const { groups, hypothesisCount } = useMemo(() => {
    const knownHypotheses = new Set(unpackedGroups.map((g) => g.hypothesis));
    const extraGroups: HypothesisGroup[] = [];

    for (const [hypothesisKey, packs] of packsByHypothesis) {
      if (hypothesisKey === NO_HYPOTHESIS_KEY || hypothesisKey === MIXED_PACK_KEY) {
        continue;
      }
      if (knownHypotheses.has(hypothesisKey)) continue;

      const firstCreativeSection =
        packs.find((p) => p.creatives.length > 0)?.creatives[0]?.section ?? null;

      extraGroups.push({
        hypothesis: hypothesisKey,
        section: firstCreativeSection,
        creatives: [],
      });
    }

    const sortByHypothesisName = (a: HypothesisGroup, b: HypothesisGroup) =>
      a.hypothesis.localeCompare(b.hypothesis);

    const mergedGroups =
      extraGroups.length === 0
        ? unpackedGroups
        : [...unpackedGroups, ...extraGroups].sort(sortByHypothesisName);

    return {
      groups: mergedGroups,
      hypothesisCount: unpackedHypothesisCount + extraGroups.length,
    };
  }, [unpackedGroups, unpackedHypothesisCount, packsByHypothesis]);

  const { orderedGroups, orderedNoHypothesis } = useOrderedHypothesisGroups({
    groups,
    noHypothesis,
    unpackedOrderByHypothesis,
  });

  const unpackedByHypothesisRef = useRef(new Map<string, string[]>());
  useEffect(() => {
    const map = new Map<string, string[]>();
    for (const group of orderedGroups) {
      map.set(
        group.hypothesis,
        group.creatives.map((creative) => creative.id),
      );
    }
    map.set(
      NO_HYPOTHESIS_KEY,
      orderedNoHypothesis.map((creative) => creative.id),
    );
    unpackedByHypothesisRef.current = map;
  }, [orderedGroups, orderedNoHypothesis]);

  const allCreativeIds = useMemo(
    () => unpackedCreatives.map((c) => c.id),
    [unpackedCreatives],
  );

  const allSelected =
    allCreativeIds.length > 0 &&
    allCreativeIds.every((id) => selectedCreatives.includes(id));
  const someSelected =
    !allSelected && allCreativeIds.some((id) => selectedCreatives.includes(id));

  const handleToggleSelectAll = useCallback(() => {
    const nextSelection = allSelected ? [] : allCreativeIds;
    updateSelection(nextSelection, { syncActivePack: false });
    datadogActions.trackDatadogAction(DATADOG_EVENT.creativeSelected, {
      selectedCount: nextSelection.length,
      totalRowsCount: allCreativeIds.length,
      creativeTab: 'test_creatives',
    });
  }, [allCreativeIds, allSelected, updateSelection]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      updateSelection(ids, { syncActivePack: false });
      datadogActions.trackDatadogAction(DATADOG_EVENT.creativeSelected, {
        selectedCount: ids.length,
        totalRowsCount: allCreativeIds.length,
        creativeTab: 'test_creatives',
      });
    },
    [allCreativeIds, updateSelection],
  );

  const selectedCreativesRef = useRef(selectedCreatives);
  useEffect(() => {
    selectedCreativesRef.current = selectedCreatives;
  }, [selectedCreatives]);

  const idsByHypothesisRef = useRef(new Map<string, string[]>());
  useEffect(() => {
    const map = new Map<string, string[]>();
    const addId = (hypothesisKey: string, creativeId: string) => {
      const existing = map.get(hypothesisKey);
      if (existing) existing.push(creativeId);
      else map.set(hypothesisKey, [creativeId]);
    };
    const ingestGroups = (groups: HypothesisGroup[]) => {
      for (const group of groups) {
        for (const c of group.creatives) {
          addId(group.hypothesis, c.id);
        }
      }
    };
    ingestGroups(groups);
    for (const c of noHypothesis) {
      addId(NO_HYPOTHESIS_KEY, c.id);
    }
    for (const pack of noHypothesisPacks) {
      for (const c of pack.creatives) {
        addId(NO_HYPOTHESIS_KEY, c.id);
      }
    }
    idsByHypothesisRef.current = map;
  }, [groups, noHypothesis, noHypothesisPacks]);

  const handleGroupSelectionChange = useCallback(
    (hypothesis: string, ids: string[]) => {
      const groupIds = idsByHypothesisRef.current.get(hypothesis) ?? [];
      const groupSet = new Set(groupIds);
      const others = selectedCreativesRef.current.filter((id) => !groupSet.has(id));
      const nextSelection = [...others, ...ids];
      updateSelection(nextSelection, { syncActivePack: false });
      datadogActions.trackDatadogAction(DATADOG_EVENT.creativeSelected, {
        selectedCount: nextSelection.length,
        totalRowsCount: allCreativeIds.length,
        creativeTab: 'test_creatives',
      });
    },
    [allCreativeIds, updateSelection],
  );

  const formContextRef = useRef(formContext);
  useEffect(() => {
    formContextRef.current = formContext;
  }, [formContext]);

  const handlePackSelectionChange = useCallback(
    (packIndex: number, ids: string[]) => {
      const ctx = formContextRef.current;
      if (!ctx) return;
      const currentPacks =
        (ctx.getValues('packsWithCreatives') as PackEntry[] | undefined) ?? [];
      const targetPack = currentPacks[packIndex];
      if (!targetPack) return;

      if (ids.length === 0) {
        const nextPacks = currentPacks.filter((_, index) => index !== packIndex);
        ctx.setValue('packsWithCreatives', nextPacks, { shouldValidate: true });
        const activeIndex = ctx.getValues('activePacksWithCreatives') as
          | number
          | null;
        if (activeIndex === packIndex) {
          ctx.setValue('activePacksWithCreatives', null);
        } else if (activeIndex !== null && activeIndex > packIndex) {
          ctx.setValue('activePacksWithCreatives', activeIndex - 1);
        }
        return;
      }

      const nextPacks = currentPacks.map((pack, index) =>
        index === packIndex ? { ...pack, creatives: ids } : pack,
      );
      ctx.setValue('packsWithCreatives', nextPacks, { shouldValidate: true });
    },
    [],
  );

  const handleCreativeMove = useCallback(
    (hypothesisKey: string, move: CreativeDnDMovePayload) => {
      const ctx = formContextRef.current;
      if (!ctx) return;

      if (move.from.kind === 'unpacked' && move.to.kind === 'unpacked') {
        const currentOrder =
          unpackedByHypothesisRef.current.get(hypothesisKey) ?? [];
        const nextOrder = [...currentOrder];
        const fromIdx =
          nextOrder[move.from.index] === move.creativeId
            ? move.from.index
            : nextOrder.indexOf(move.creativeId);
        if (fromIdx < 0) return;
        const [removed] = nextOrder.splice(fromIdx, 1);
        const insertAt = Math.min(Math.max(0, move.to.index), nextOrder.length);
        nextOrder.splice(insertAt, 0, removed);
        setUnpackedOrderByHypothesis((prev) => ({
          ...prev,
          [hypothesisKey]: nextOrder,
        }));
        return;
      }

      const currentPacks =
        (ctx.getValues('packsWithCreatives') as PackEntry[] | undefined) ?? [];
      const movedPacks = applyPackCreativesMove(currentPacks, move);
      const { packs: nextPacks, removedIndices } = pruneEmptyPacks(movedPacks);
      ctx.setValue('packsWithCreatives', nextPacks, { shouldValidate: true });
      if (removedIndices.length > 0) {
        const activeIndex = ctx.getValues('activePacksWithCreatives') as
          | number
          | null;
        const nextActive = adjustActivePackIndexAfterRemoval(
          activeIndex,
          removedIndices,
        );
        if (nextActive !== activeIndex) {
          ctx.setValue('activePacksWithCreatives', nextActive);
        }
      }

      if (move.to.kind === 'unpacked') {
        const currentOrder =
          unpackedByHypothesisRef.current.get(hypothesisKey) ?? [];
        const without = currentOrder.filter((id) => id !== move.creativeId);
        const nextOrder = [...without];
        const insertAt = Math.min(Math.max(0, move.to.index), nextOrder.length);
        nextOrder.splice(insertAt, 0, move.creativeId);
        setUnpackedOrderByHypothesis((prev) => ({
          ...prev,
          [hypothesisKey]: nextOrder,
        }));
        return;
      }

      if (move.from.kind === 'unpacked') {
        setUnpackedOrderByHypothesis((prev) => {
          const existing = prev[hypothesisKey];
          if (!existing?.length) return prev;
          const nextOrder = existing.filter((id) => id !== move.creativeId);
          return { ...prev, [hypothesisKey]: nextOrder };
        });
      }
    },
    [setUnpackedOrderByHypothesis],
  );

  const sortedFlatCreatives = useMemo(
    () =>
      [...displayCreatives].sort((a, b) => {
        if (a.launch_status === 'launched' && b.launch_status !== 'launched') return -1;
        if (a.launch_status !== 'launched' && b.launch_status === 'launched') return 1;
        return new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime();
      }),
    [displayCreatives],
  );

  const hasActiveFilters = useFiltersActive(filters, uploadedFilter);

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const isInitialLoading = isLoading;
  const isRefetching = !isLoading && isFetching;
  const totalCreatives = displayCreatives.length;
  const hasResults = totalCreatives > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, width: '100%' }}>
      <TestCreativesHeader
        totalCreatives={totalCreatives}
        hypothesisCount={hypothesisCount}
        groupByHypothesis={groupByHypothesis}
        onGroupByHypothesisChange={setGroupByHypothesis}
        allSelected={allSelected}
        someSelected={someSelected}
        onToggleSelectAll={handleToggleSelectAll}
        columnOrder={columnOrder}
        visibilityModel={visibilityModel}
        onToggleColumn={toggleColumn}
        onMoveColumn={moveColumn}
        onResetColumns={resetDefaults}
        saveFiltersEnabled={saveFiltersEnabled}
        saveFiltersHasSaved={saveFiltersHasSaved}
        saveFiltersDirty={saveFiltersDirty}
        savedAtLabel={savedAtLabel}
        savedFiltersSummaryLines={savedSummaryLines}
        onEnableSaveFilters={handleEnableSaveFilters}
        onDisableSaveFilters={handleDisableSaveFilters}
        onUpdateSaveFilters={updateSaved}
        onClearSaveFilters={handleClearSaveFilters}
      />

      {/* Filter bar — stays mounted and interactive while the grid below refetches */}
      <Box sx={{ mb: 2.5 }}>
        <CreativesFilterBar
          search={filters.search}
          uploaded={uploadedFilter}
          hypothesis={filters.hypothesis}
          flow={filters.flow}
          customer={filters.customer}
          technology={filters.technology}
          tpcFinishDate={filters.tpcFinishDate}
          status={filters.status}
          filterOptions={filterOptions}
          onSearchChange={setSearch}
          onUploadedChange={setUploadedFilter}
          onHypothesisChange={setHypothesis}
          onFlowChange={setFlow}
          onCustomerChange={setCustomer}
          onTechnologyChange={setTechnology}
          onTpcFinishDateChange={setTpcFinishDate}
          onStatusChange={setStatus}
        />
      </Box>

      {/* Grid / groups — fallback shows ONLY here while filters refetch */}
      <LoadingOverlay
        isLoading={isRefetching}
        title="Loading test creatives..."
      >
        <TestCreativesContent
          isInitialLoading={isInitialLoading}
          hasResults={hasResults}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          groupByHypothesis={groupByHypothesis}
          packsByHypothesis={packsByHypothesis}
          groups={orderedGroups}
          noHypothesis={orderedNoHypothesis}
          sortedFlatCreatives={sortedFlatCreatives}
          selectedCreatives={selectedCreatives}
          columns={visibleColumns}
          dragEnabled={dragEnabled}
          canEditPackTag={canEditPackTag}
          packTagsByIndex={canEditPackTag ? packTagsByIndex : undefined}
          mixedPackCreativeNameTagsById={creativeNameTagsById}
          onGroupSelectionChange={handleGroupSelectionChange}
          onPackSelectionChange={handlePackSelectionChange}
          onActivatePack={canEditPackTag ? handleActivatePack : undefined}
          onFlatSelectionChange={handleSelectionChange}
          onCreativeMove={handleCreativeMove}
        />
      </LoadingOverlay>
    </Paper>
  );
};

export default TestCreativesList;
